// src/app/services/badges.service.ts
import { Injectable } from "@angular/core";
import moment from "moment";
import { RosterService } from "../roster/roster.service";

/**
 * PreprocessFn:
 *   - Accepts the raw account object (or any other input you need)
 *   - Returns an object containing exactly the arrays or properties that ruleFns expect
 */
type PreprocessFn = (acc: any) => Record<string, any>;

/**
 * BadgeRuleFn:
 *   - Takes the “preprocessed” context from PreprocessFn
 *   - Returns a number = how many badges this rule contributes
 */
type BadgeRuleFn = (ctx: Record<string, any>) => number;

/**
 * Each page’s configuration holds:
 * 1) preprocess(acc) => ctx
 * 2) rules: BadgeRuleFn[]
 */
interface BadgeConfig {
  preprocess: PreprocessFn;
  rules: BadgeRuleFn[];
}

// Release to the client -> releaseToStatus = 1
// Client approves -> clientStatus = 1
// Client declines -> clientStatus = 2
// Release to the supplier -> releaseToStatus = 2
// Supplier pending -> supplierStatus = 1
// Supplier approves -> supplierStatus = 2
// Supplier declines -> supplierStatus = 3
// Release to the resource -> releaseToStatus = 3
// Resource pending -> resourceStatus = 1
// Resource approves -> resourceStatus = 2
// Resource declines -> resourceStatus = 3

@Injectable({
  providedIn: "root"
})
export class BadgesService {
  private shifts: any[] = [];

  constructor(private rosterService: RosterService) {
    this.rosterService.shifts$.subscribe((shifts) => {
      this.shifts = shifts;
    });
  }

  // 1) Helper: is this shift still upcoming in its venue time zone?
  isUpcoming = (item: any, timezone?) => {
    const tzId = item.venueTime?.timeZoneId || timezone || "UTC";
    if (typeof tzId !== "string") return false;
    const nowAtVenue = moment().tz(tzId).valueOf();
    const shiftLocalTs = moment.tz(item.task.start_time, tzId).valueOf();
    return shiftLocalTs >= nowAtVenue;
  };

  // 2) Per‑role rule predicates
  subscriberRule = (item: any) => {
    const {
      release_to_status: rt,
      client_status: c,
      supplier_status: s,
      resource_status: r
    } = item.status_codes;

    // 1) client acted on "released→client"
    if (rt === 1 && (c === 1 || c === 2)) return true;
    // 2) supplier acted on "released→supplier" after client approved
    if (rt === 2 && c === 1 && (s === 2 || s === 3)) return true;
    // 3) resource declined on "released→resource"
    if (rt === 3 && c === 1 && s === 2 && r === 3) return true;
    // 4) shift not acted on yet
    if (rt === 0 && c === 0 && s === 0 && r === 0) return true;
    // 5) client approved, supplier approved, resource not pending
    if (rt === 2 && c === 1 && s === 2 && r === 0) return true;
    return false;
  };

  clientRule = (item: any) => {
    const {
      release_to_status: rt,
      client_status: c,
      supplier_status: s,
      resource_status: r
    } = item.status_codes;
    // released→client, but client hasn't acted yet
    return rt === 1 && c === 0;
  };

  supplierRule = (item: any) => {
    const {
      release_to_status: rt,
      client_status: c,
      supplier_status: s,
      resource_status: r
    } = item.status_codes;
    // released→supplier, client approved, but supplier hasn't acted yet
    if (s === 1) return true;
    // resource declined
    if (s === 2 && r === 3) return true;
  };

  // ─────────────────────────────────────────────────────────────────
  // 1) badgeConfigMap: one entry per "pageName"
  // ─────────────────────────────────────────────────────────────────
  private readonly badgeConfigMap: Record<string, BadgeConfig> = {
    // ─── "clients" page
    clients: {
      // preprocess: pull out acc.clients (or default to empty array)
      preprocess: (acc: any) => ({
        clients: Array.isArray(acc.clients) ? acc.clients : []
      }),
      // rules: just count items with connection_status === 0
      rules: [
        (ctx) => {
          return ctx.clients.filter((c: any) => c.connection_status === 0)
            .length;
        }
      ]
    },

    // ─── "suppliers" page
    suppliers: {
      preprocess: (acc: any) => ({
        suppliers: Array.isArray(acc.suppliers) ? acc.suppliers : []
      }),
      rules: [
        (ctx) => {
          return ctx.suppliers.filter((s: any) => s.connection_status === 0)
            .length;
        }
      ]
    },

    // ─── "resources" page
    resources: {
      preprocess: (acc: any) => {
        // (A) Normalize acc.resources
        const processedResources: any[] = Array.isArray(acc.resources)
          ? acc.resources.map((r: any) => ({
              ...r,
              supplier: { name: "Direct" }
            }))
          : [];

        // (B) Normalize acc.invitations
        const processedInvites: any[] = Array.isArray(acc.invitations)
          ? acc.invitations.map((inv: any) => ({
              ...inv,
              supplier: { name: "Direct" }
            }))
          : [];

        // (C) Build concatResources exactly as you did in the component:
        //     include all processedResources, plus those invites whose status !== 1
        const concatResources: any[] = [
          ...processedResources,
          ...processedInvites.filter((inv) => inv.status !== 1)
        ];

        // Return the context object that rules will read
        return {
          resources: processedResources,
          resourceInvites: processedInvites,
          concatResources
        };
      },
      rules: [
        // ▶ Rule #1: pending "connections" in resources + resourceInvites
        (ctx) => {
          let pendingConnections = 0;
          pendingConnections += ctx.resources.filter(
            (r: any) => r.status === 0
          ).length;
          pendingConnections += ctx.resourceInvites.filter(
            (inv: any) => inv.status === 0
          ).length;

          return pendingConnections;
        },
        // ▶ Rule #2: pending accreditation_requests inside each resource
        (ctx) => {
          let pendingAccreds = 0;
          for (const r of ctx.resources) {
            if (Array.isArray(r.accreditation_requests)) {
              pendingAccreds += r.accreditation_requests.filter(
                (req: any) => req.status === 0
              ).length;
            }
          }
          return pendingAccreds;
        }
      ]
    },
    "incident-reports": {
      preprocess: (acc) => {
        const shifts = this.shifts || acc.shifts || [];
        return { shifts, account: acc };
      },
      rules: [
        (ctx) => {
          return ctx.shifts
            .flatMap((shift) =>
              Array.isArray(shift.task?.form_data) ? shift.task.form_data : []
            )
            .filter((item) => item.type === "Incident Report").length;
        }
      ]
    },
    roster: {
      preprocess: (acc) => {
        const shifts = this.shifts || acc.shifts || [];
        return { shifts, account: acc };
      },
      rules: [
        (ctx) => {
          const acc = ctx.account;
          if (!acc) return 0;

          // 3) Pick the right predicate based on role
          let ruleFn: ((item: any) => boolean) | null = null;
          if (acc.isSubscriber) {
            ruleFn = this.subscriberRule;
          } else if (acc.isClient) {
            ruleFn = this.clientRule;
          } else if (acc.isSupplier) {
            ruleFn = this.supplierRule;
          }
          if (!ruleFn) {
            return 0;
          }

          return ctx.shifts.filter(
            (item) => this.isUpcoming(item) && ruleFn!(item)
          ).length;
        }
      ]
    },
    "account-details": {
      preprocess: (acc) => {
        const accountInvites = [
          ...acc?.clients.filter(
            (c) =>
              c.connection_status === 0 &&
              c.connection_request.request_account_uuid !== ""
          ),
          ...acc?.suppliers.filter(
            (s) =>
              s.connection_status === 0 &&
              s.connection_request.request_account_uuid !== ""
          )
        ];
        return { accountInvites, account: acc };
      },
      rules: [
        (ctx) => {
          if (!ctx.accountInvites || ctx.accountInvites.length === 0) {
            return 0;
          }
          return ctx.accountInvites.filter(
            (invite: any) =>
              invite?.connection_request?.request_account_uuid !==
              ctx.account?.uuid
          ).length;
        }
      ]
    },
    "timesheet-register": {
      preprocess: (acc) => {
        const shifts = this.shifts || acc.shifts || [];
        return { shifts, account: acc }; // provide shifts & account context
      },
      rules: [
        (ctx) => {
          const isPending = (item: any) => {
            const resource = item?.resource || {};
            const timesheet = item?.timesheet || {};
            const releaseAccepted = resource?.release_status === 2;
            const signonSigned = !!timesheet?.signon?.signature;
            const signoffSigned = !!timesheet?.signoff?.signature;

            // Count if not accepted OR not fully signed off
            return !releaseAccepted || !(signonSigned && signoffSigned);
          };

          let total = 0;
          ctx.shifts.forEach((shift: any) => {
            if (Array.isArray(shift.tasks)) {
              total += shift.tasks.filter(isPending).length;
            } else if (shift.task) {
              total += isPending(shift.task) ? 1 : 0;
            }
          });
          return total;
        }
      ]
    }

    // ─── You can add as many pages as you like below:
    // tasks: { preprocess: acc => ({ tasks: … }), rules: [ ctx => /*…*/ ] },
  };

  // ─────────────────────────────────────────────────────────────────
  // 2) pageBadges(acc, pageName):
  //    Look up that page's config, run preprocess, then sum all rule results.
  // ─────────────────────────────────────────────────────────────────
  public navBadges(acc: any, navItemName: string): string {
    const config = this.badgeConfigMap[navItemName];
    if (!config) {
      // No such page configured → return zero
      return "0";
    }

    // (A) Run the page-specific preprocessing
    let ctx: Record<string, any>;
    try {
      ctx = config.preprocess(acc);
    } catch (e) {
      console.warn(
        `BadgesService: error in preprocess for "${navItemName}"`,
        e
      );
      ctx = {};
    }

    // (B) Execute each rule on that context, summing its return value
    const totalCount = config.rules.reduce((sum, ruleFn) => {
      try {
        const n = ruleFn(ctx);
        return sum + (typeof n === "number" ? n : 0);
      } catch (err) {
        console.warn(`BadgesService: error in rule for "${navItemName}"`, err);
        return sum;
      }
    }, 0);
    return totalCount.toString();
  }

  resourceAccreditationRequestsBadges(resource: any): string {
    if (!resource || typeof resource !== "object") {
      return "0";
    }

    const requests = Array.isArray(resource.accreditation_requests)
      ? resource.accreditation_requests
      : [];

    const pendingCount = requests.filter((req: any) => req.status === 0).length;

    return pendingCount.toString();
  }

  countBadgesForShiftsOfClients(
    clients: any[],
    shifts: any[],
    isSubscriber: any
  ) {
    if (!isSubscriber) return [];

    if (!Array.isArray(clients) || !Array.isArray(shifts)) {
      return [];
    }
    const result = clients.map((client: any) => {
      const clientUuid = client.uuid;
      const clientShifts = shifts.filter(
        (shift: any) => shift.client_uuid === clientUuid
      );

      const badgeCount = clientShifts.filter(
        (shift: any) => this.isUpcoming(shift) && this.subscriberRule(shift)
      ).length;

      return { uuid: clientUuid, count: badgeCount };
    });
    return result;
  }

  countBadgesForShiftsByVenues(
    clients: any[],
    shifts: any[],
    isSubscriber: any,
    isClient: any
  ) {
    if (!isSubscriber && !isClient) return [];

    if (!Array.isArray(clients) || !Array.isArray(shifts)) {
      return [];
    }
    const venues = clients
      .map((client: any) => client.venues.map((v: any) => v))
      .flat();

    if (!Array.isArray(venues) || !Array.isArray(shifts)) {
      return [];
    }

    const result = venues.map((venue: any) => {
      const venueId = venue.uuid;
      const venueShifts = shifts.filter(
        (shift: any) => shift.venue.uuid === venueId
      );

      const badgeCount = venueShifts.filter(
        (shift: any) =>
          this.isUpcoming(shift) &&
          (isSubscriber ? this.subscriberRule(shift) : this.clientRule(shift))
      ).length;

      return { uuid: venueId, count: badgeCount };
    });
    return result;
  }

  incidentReportsClientListBadges(shifts: any, clientList: any) {
    if (!Array.isArray(shifts) || !Array.isArray(clientList)) {
      return [];
    }

    return clientList.map((client) => {
      const clientShifts = shifts.filter(
        (shift) => shift.client_uuid === client.uuid
      );

      const incidentCount = clientShifts
        .flatMap((shift) =>
          Array.isArray(shift.task?.form_data) ? shift.task.form_data : []
        )
        .filter((item) => item.type === "Incident Report").length;

      return {
        clientUuid: client.uuid,
        badgeCount: incidentCount
      };
    });
  }

  incidentReportsVenueListBadges(shifts: any, clientList: any) {
    if (!Array.isArray(clientList) || clientList.length === 0) {
      return [];
    }

    const venues = clientList
      .map((client: any) => client?.venues?.map((v: any) => v))
      .flat();

    if (!Array.isArray(venues) || !Array.isArray(shifts)) {
      return [];
    }

    return clientList.flatMap((client: any) => {
      if (!Array.isArray(client.venues)) return [];
      return client?.venues?.map((venue: any) => {
        const venueShifts = shifts.filter(
          (shift) =>
            shift.venue?.uuid === venue.uuid &&
            shift.client_uuid === client.uuid
        );
        const incidentCount = venueShifts
          .flatMap((shift) =>
            Array.isArray(shift.task?.form_data) ? shift.task.form_data : []
          )
          .filter((item) => item.type === "Incident Report").length;

        return {
          venueUuid: venue.uuid,
          badgeCount: incidentCount
        };
      });
    });
  }
}
